import { reactive } from 'vue';
import { permissionDeniedOf, permissionDeniedText } from '../api/staffErrors';

/** 提示条类型 */
export type ToastKind = 'success' | 'error' | 'info';

/** 提示条上的操作按钮 (如 "撤销") */
export interface ToastAction {
  /** 按钮文字 */
  label: string;
  /** 点击后执行; 执行后提示条关闭 */
  run: () => void;
}

/** 一条提示 */
export interface ToastItem {
  /** 自增 ID */
  id: number;
  /** 类型 */
  kind: ToastKind;
  /** 显示文字 */
  text: string;
  /** 操作按钮 (可空) */
  action?: ToastAction;
}

const DURATION_MS: Record<ToastKind, number> = { success: 2500, info: 3000, error: 5000 };
/** 带操作按钮的提示停留更久, 留出点按时间 */
const ACTION_DURATION_MS = 6000;

/** 全局提示队列 (模块级单例, 由 AppToaster 渲染) */
export const toastState = reactive<{ items: ToastItem[] }>({ items: [] });

let seq = 0;

/** 移除一条提示 */
export function dismissToast(id: number) {
  const i = toastState.items.findIndex((t) => t.id === id);
  if (i >= 0) toastState.items.splice(i, 1);
}

function push(kind: ToastKind, text: string, action?: ToastAction) {
  // 同一句错误正在显示时只留一条 (并发请求同时失败, 全局拦截与页面都会提示)
  if (kind === 'error' && toastState.items.some((t) => t.kind === 'error' && t.text === text)) return;
  const id = ++seq;
  toastState.items.push({ id, kind, text, action });
  if (toastState.items.length > 4) toastState.items.shift();
  window.setTimeout(() => dismissToast(id), action ? ACTION_DURATION_MS : DURATION_MS[kind]);
}

/** 点提示条上的操作按钮: 先关闭提示条再执行 */
export function runToastAction(id: number) {
  const item = toastState.items.find((t) => t.id === id);
  if (!item?.action) return;
  dismissToast(id);
  item.action.run();
}

/** 全局拦截到 403 permissionDenied 时提示一次 */
export function notifyPermissionDenied(permission: string) {
  push('error', permissionDeniedText(permission || null));
}

/**
 * 把接口错误整理成给员工看的文字: 去掉 "HTTP 400:" 前缀, 解析 ProblemDetails 里的 detail/title/errors.
 */
export function friendlyError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e ?? '');
  const m = raw.match(/^HTTP (\d{3}):\s*([\s\S]*)$/);
  const status = m ? Number(m[1]) : null;
  const body = (m ? m[2] : raw).trim();
  if (status !== null) {
    const denied = permissionDeniedOf(status, body);
    if (denied !== null) return permissionDeniedText(denied || null);
  }
  if (body.startsWith('{')) {
    try {
      const j = JSON.parse(body) as { message?: string; detail?: string; title?: string; errors?: Record<string, string[]> | { description?: string }[] };
      if (Array.isArray(j.errors) && j.errors.length) {
        return j.errors.map((x) => x.description).filter(Boolean).join('; ') || j.detail || j.title || body;
      }
      if (j.errors && typeof j.errors === 'object') {
        const msgs = Object.values(j.errors).flat().filter(Boolean);
        if (msgs.length) return msgs.join('; ');
      }
      if (j.message) return j.message;
      if (j.detail) return j.detail;
      if (j.title) return j.title;
    } catch {
      // 非 JSON 正文按原文展示
    }
  }
  if (status === 403) return '没有权限执行此操作';
  if (status === 404) return '内容不存在或已被删除';
  if (status !== null && status >= 500) return `服务器出错了, 请稍后再试${body ? ` (${body.slice(0, 80)})` : ''}`;
  if (/Failed to fetch|NetworkError/i.test(body)) return '网络连接失败, 请检查网络后重试';
  return body || '操作失败';
}

/** 全局提示: success / error / info; success / info 可带一个操作按钮 */
export function useToast() {
  return {
    success: (text: string, action?: ToastAction) => push('success', text, action),
    info: (text: string, action?: ToastAction) => push('info', text, action),
    error: (textOrError: unknown) =>
      push('error', typeof textOrError === 'string' ? textOrError : friendlyError(textOrError)),
  };
}
